import { useCallback, useEffect, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import usePageBuilderStore from '../stores/pageBuilderStore'
import { useLanguageStore } from '../stores/languageStore'

export function useBlockBuilder({
  entityType,
  entityId,
  isNew,
  blockField,
  selectedTemplateId = null,
}) {
  const queryClient = useQueryClient()
  const { currentLanguageCode: locale } = useLanguageStore()

  const viewMode = usePageBuilderStore((state) => state.viewMode)
  const draftStates = usePageBuilderStore((state) => state.draftStates)
  const localBlocks = usePageBuilderStore((state) => state.localBlocks)
  const selectedBlockId = usePageBuilderStore((state) => state.selectedBlockId)
  const hasUnsavedChanges = usePageBuilderStore(
    (state) => state.hasUnsavedChanges,
  )
  const hasDraftStates = usePageBuilderStore((state) => state.hasDraftStates)
  const isViewModeChanging = usePageBuilderStore(
    (state) => state.isViewModeChanging,
  )
  const isLocaleChanging = usePageBuilderStore(
    (state) => state.isLocaleChanging,
  )

  const setSelectedBlockId = usePageBuilderStore(
    (state) => state.setSelectedBlockId,
  )
  const setDraftStates = usePageBuilderStore((state) => state.setDraftStates)
  const updateDraftField = usePageBuilderStore(
    (state) => state.updateDraftField,
  )
  const clearLocalBlocks = usePageBuilderStore(
    (state) => state.clearLocalBlocks,
  )
  const addLocalBlock = usePageBuilderStore((state) => state.addLocalBlock)
  const setHasUnsavedChanges = usePageBuilderStore(
    (state) => state.setHasUnsavedChanges,
  )
  const setHasDraftStates = usePageBuilderStore(
    (state) => state.setHasDraftStates,
  )
  const templateHydratedIdRef = useRef(null)

  const entityIdParam =
    blockField || (entityType === 'page' ? 'page_id' : 'post_id')

  const {
    data: blocksRes,
    isLoading: blocksLoading,
    error: blocksError,
  } = useQuery({
    queryKey: [
      'blocks',
      { [entityIdParam]: entityId, view_mode: viewMode, locale },
    ],
    queryFn: () =>
      api
        .get(`/blocks`, {
          params: {
            [entityIdParam]: entityId,
            per_page: 100,
            view_mode: viewMode,
            locale: locale,
          },
        })
        .then((r) => r.data),
    enabled: !!entityId && !isNew && !!locale,
  })

  const { data: blockTypesRes } = useQuery({
    queryKey: ['block-types', 'all'],
    queryFn: () =>
      api
        .get('/block-types', { params: { per_page: 100 } })
        .then((r) => r.data),
  })

  const blockTypes = blockTypesRes?.data || []

  const { data: templateBlocksRes } = useQuery({
    queryKey: ['template-blocks', selectedTemplateId, locale],
    queryFn: () =>
      api
        .get('/blocks', {
          params: {
            content_template_id: selectedTemplateId,
            per_page: 100,
            view_mode: 'live',
            locale: locale,
          },
        })
        .then((r) => r.data),
    enabled:
      isNew && entityType === 'product' && !!selectedTemplateId && !!locale,
  })

  const fixedBlocksForNew = useMemo(() => {
    if (!isNew) return []

    return blockTypes
      .filter((bt) => {
        if (!bt.is_fixed || !bt.is_active) return false
        const entityTypes = bt.fixed_entity_types || []
        if (entityTypes.length === 0) return true
        return entityTypes.includes(entityType)
      })
      .map((bt, index) => ({
        id: `fixed-${bt.id}`,
        block_type_id: bt.id,
        name: bt.name,
        content: {},
        settings: bt.default_settings || {},
        sort_order: 1000 + index,
        is_active: true,
        is_fixed: true,
        block_type: bt,
      }))
  }, [isNew, blockTypes, entityType])

  const blocks = isNew ? fixedBlocksForNew : blocksRes?.data || []

  const cleanBlocks = blocks.map((block) => {
    if (
      block.revision_content &&
      typeof block.revision_content === 'object' &&
      Object.keys(block.revision_content).length > 100
    ) {
      return {
        ...block,
        revision_content: null,
        has_revision: false,
      }
    }
    return block
  })

  const allBlocks = [...cleanBlocks, ...localBlocks]

  useEffect(() => {
    if (!isNew || entityType !== 'product') return

    if (!selectedTemplateId) {
      if (templateHydratedIdRef.current !== null) {
        clearLocalBlocks()
        templateHydratedIdRef.current = null
      }
      return
    }

    if (templateHydratedIdRef.current === selectedTemplateId) {
      return
    }

    const templateBlocks = templateBlocksRes?.data || []
    if (!Array.isArray(templateBlocks) || templateBlocks.length === 0) {
      return
    }

    clearLocalBlocks()

    templateBlocks.forEach((block, index) => {
      addLocalBlock({
        id: `tpl-${selectedTemplateId}-${block.id}-${Date.now()}-${index}`,
        [entityIdParam]: null,
        block_type_id: Number(block.block_type_id),
        name: block.name,
        content: block.content || {},
        settings: block.settings || {},
        sort_order: Number(block.sort_order ?? index),
        locale: locale,
        is_local: true,
        block_type: block.block_type,
      })
    })

    templateHydratedIdRef.current = selectedTemplateId
  }, [
    isNew,
    entityType,
    selectedTemplateId,
    templateBlocksRes,
    clearLocalBlocks,
    addLocalBlock,
    entityIdParam,
    locale,
  ])

  const sortedBlocks = useMemo(() => {
    const regularBlocks = allBlocks.filter((b) => !b.is_fixed)
    const fixedBlocks = allBlocks.filter((b) => b.is_fixed)

    regularBlocks.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    fixedBlocks.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))

    return [...regularBlocks, ...fixedBlocks]
  }, [allBlocks])

  const createBlock = useMutation({
    mutationFn: async (blockData) => {
      if (isNew) {
        const newBlock = {
          id: `local-${Date.now()}-${Math.random()}`,
          [entityIdParam]: null,
          block_type_id: Number(blockData.block_type_id),
          name: blockData.name,
          content: blockData.content,
          settings: blockData.settings,
          sort_order: blockData.sort_order,
          locale: locale,
          is_local: true,
        }
        addLocalBlock(newBlock)
        return newBlock
      } else {
        const payload = {
          [entityIdParam]: Number(entityId),
          block_type_id: Number(blockData.block_type_id),
          name: blockData.name,
          content: blockData.content,
          settings: blockData.settings,
          sort_order: blockData.sort_order,
          locale: locale,
        }

        const response = await api.post('/blocks', payload)
        return response.data
      }
    },
    onSuccess: (newBlock) => {
      if (!isNew) {
        queryClient.invalidateQueries({
          queryKey: [
            'blocks',
            { [entityIdParam]: entityId, view_mode: viewMode, locale },
          ],
        })
      }
      setSelectedBlockId(newBlock.id)
    },
  })

  const updateBlock = useMutation({
    mutationFn: async ({ blockId, data }) => {
      const response = await api.put(`/blocks/${blockId}`, {
        ...data,
        locale: locale,
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'blocks',
          { [entityIdParam]: entityId, view_mode: viewMode, locale },
        ],
      })
    },
  })

  const detachPresetBlock = useMutation({
    mutationFn: async (blockId) => {
      const response = await api.post(`/blocks/${blockId}/detach-preset`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'blocks',
          { [entityIdParam]: entityId, view_mode: viewMode, locale },
        ],
      })
    },
  })

  const deleteBlock = useMutation({
    mutationFn: async (blockId) => {
      await api.delete(`/blocks/${blockId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'blocks',
          { [entityIdParam]: entityId, view_mode: viewMode, locale },
        ],
      })
      setSelectedBlockId(null)
    },
  })

  const handleFieldChange = useCallback(
    (blockId, field, value) => {
      if (viewMode === 'draft') {
        updateDraftField(blockId, field, value)
      } else {
        updateBlock.mutate({
          blockId,
          data: { [field]: value },
        })
      }
    },
    [viewMode, updateDraftField, updateBlock],
  )

  const saveAllDrafts = useMutation({
    mutationFn: async () => {
      const localBlockIds = new Set(localBlocks.map((b) => b.id))

      const revisionPromises = Object.entries(draftStates)
        .filter(
          ([blockId]) =>
            !localBlockIds.has(blockId) &&
            !String(blockId).startsWith('local-'),
        )
        .map(([blockId, draftFields]) => {
          const originalBlock = blocks.find((b) => b.id === Number(blockId))
          if (!originalBlock) return null

          const baseContent =
            viewMode === 'draft'
              ? originalBlock?.latest_revision?.content ||
                originalBlock?.content ||
                {}
              : originalBlock?.content || {}

          const mergedContent = { ...baseContent, ...draftFields }

          return api.post(`/blocks/${blockId}/revisions`, {
            content: mergedContent,
            locale: locale,
          })
        })
        .filter(Boolean)

      const localBlockPromises = localBlocks.map((block) => {
        const localDraft = draftStates[block.id] || {}
        const mergedLocalContent = { ...block.content, ...localDraft }
        return api.post('/blocks', {
          [entityIdParam]: Number(entityId),
          block_type_id: Number(block.block_type_id),
          preset_id: block.preset_id || null,
          is_preset_synced: !!block.is_preset_synced,
          name: block.name,
          content: mergedLocalContent,
          settings: block.settings,
          sort_order: block.sort_order,
          locale: locale,
        })
      })

      return Promise.all([...revisionPromises, ...localBlockPromises])
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: [
          'blocks',
          { [entityIdParam]: entityId, view_mode: viewMode, locale },
        ],
      })
      setDraftStates({})
      clearLocalBlocks()
      setHasUnsavedChanges(false)
    },
  })

  const publishAllDrafts = useMutation({
    mutationFn: async () => {
      return api.post('/blocks/publish-all-revisions', {
        [entityIdParam]: entityId,
        locale: locale,
      })
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: [
          'blocks',
          { [entityIdParam]: entityId, view_mode: viewMode, locale },
        ],
      })
      setDraftStates({})
      setHasUnsavedChanges(false)
    },
  })

  const handleSaveDrafts = useCallback(() => {
    if (Object.keys(draftStates).length === 0 && localBlocks.length === 0) {
      return
    }
    saveAllDrafts.mutate()
  }, [draftStates, localBlocks, saveAllDrafts])

  const handlePublish = useCallback(async () => {
    if (Object.keys(draftStates).length > 0) {
      try {
        await saveAllDrafts.mutateAsync()
        publishAllDrafts.mutate()
      } catch (error) {}
    } else {
      publishAllDrafts.mutate()
    }
  }, [draftStates, saveAllDrafts, publishAllDrafts])

  const handleDiscard = useCallback(async () => {
    setDraftStates({})
    setHasUnsavedChanges(false)

    const blocksWithRevisions = blocks.filter(
      (block) => block.latest_revision !== null,
    )

    if (blocksWithRevisions.length > 0) {
      const deletePromises = blocksWithRevisions.map((block) => {
        return api.delete(`/blocks/${block.id}/unpublished-revisions`, {
          params: { locale: locale },
        })
      })

      try {
        await Promise.all(deletePromises)
        await new Promise((resolve) => setTimeout(resolve, 500))
        await queryClient.invalidateQueries({ queryKey: ['blocks'] })
        await queryClient.refetchQueries({
          queryKey: [
            'blocks',
            { [entityIdParam]: entityId, view_mode: viewMode, locale },
          ],
        })
      } catch (error) {}
    } else {
      queryClient.invalidateQueries({
        queryKey: [
          'blocks',
          { [entityIdParam]: entityId, view_mode: viewMode, locale },
        ],
      })
    }
  }, [
    draftStates,
    queryClient,
    entityId,
    viewMode,
    locale,
    setHasUnsavedChanges,
    blocks,
    entityIdParam,
  ])

  const handleDetachPreset = useCallback(
    (blockId) => {
      if (String(blockId).startsWith('local-')) {
        return
      }
      detachPresetBlock.mutate(blockId)
    },
    [detachPresetBlock],
  )

  useEffect(() => {
    if (isViewModeChanging || isLocaleChanging || blocksLoading) return

    const hasChanges =
      Object.keys(draftStates).length > 0 ||
      localBlocks.length > 0 ||
      Object.entries(draftStates).some(([blockId, content]) => {
        const originalBlock = blocks.find((b) => b.id === Number(blockId))
        if (!originalBlock) return false

        const originalContent =
          viewMode === 'draft'
            ? originalBlock.latest_revision?.content ||
              originalBlock.content ||
              {}
            : originalBlock.content || {}

        return JSON.stringify(content) !== JSON.stringify(originalContent)
      })

    setHasUnsavedChanges(hasChanges)
  }, [
    draftStates,
    localBlocks,
    blocks,
    isViewModeChanging,
    blocksLoading,
    viewMode,
    isLocaleChanging,
    setHasUnsavedChanges,
  ])

  useEffect(() => {
    if (isViewModeChanging || isLocaleChanging || blocksLoading) return

    const hasLocalDrafts =
      Object.keys(draftStates).length > 0 || localBlocks.length > 0
    const hasExistingRevisions = blocks.some(
      (block) => block.latest_revision !== null,
    )
    const shouldEnableDiscard = hasLocalDrafts || hasExistingRevisions

    setHasDraftStates(shouldEnableDiscard)
  }, [
    blocks,
    draftStates,
    localBlocks,
    setHasDraftStates,
    isViewModeChanging,
    isLocaleChanging,
    blocksLoading,
  ])

  return {
    blocks: sortedBlocks,
    blocksLoading,
    blocksError,
    selectedBlockId,
    hasUnsavedChanges,
    hasDraftStates,
    viewMode,
    locale,
    draftStates,
    localBlocks,
    setSelectedBlockId,
    createBlock,
    updateBlock,
    deleteBlock,
    handleFieldChange,
    handleSaveDrafts,
    handlePublish,
    handleDiscard,
    handleDetachPreset,
    isSaving: saveAllDrafts.isPending,
    isPublishing: publishAllDrafts.isPending,
  }
}
